import React, { useEffect, useState, useMemo } from "react";
import { Box, Button, Divider, Modal, FormControl, InputAdornment, OutlinedInput, Badge, FormControlLabel, Checkbox, Typography } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import { useDispatch, useSelector } from 'react-redux';
import { SET_SCHEME_FOR_BASKET } from '../../../redux-store/types/globalVariables';
import { RecommendedSchemesModal, SubFundCategoryCard } from "./investor-transaction-helper";
import Filters, { filterDataExplore } from "./filterComponent";
import { SchemeList, SubAssetClasses } from "../../../redux-store/types/api-types";
import { TabContext } from '@mui/lab';
import type { Dispatch } from "redux";
import { useLocation } from "react-router";

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = window.setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

export default function Scheme_List({
  handleBackClick,
  selectedSubAssetClass,
  setSelectedSubAssetClass,
  handleSetViewSchemeDetails,
  sub_asset_classes,
  schemeList,
  investorId,
  investorName,
  modeOfHolding
}: {
  handleBackClick: () => void;
  selectedSubAssetClass: SubAssetClasses | undefined;
  setSelectedSubAssetClass: any;
  handleSetViewSchemeDetails: (val: boolean, schemeDetail: SchemeList) => void;
  sub_asset_classes: SubAssetClasses[] | undefined;
  schemeList: SchemeList[] | undefined;
  investorId: string;
  investorName: string;
  modeOfHolding?: number | null;
}) {

  const dispatch = useDispatch();
  const globalVariables = useSelector((state: any) => state.globalVariable);
  const [value, setValue] = useState<string>("Short Term");
  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce(search, 300);
  const [AMCList, setAMCList] = useState<{ AMC: string, AMCId: number }[]>([]);
  const [filterData, setFilterData] = useState<filterDataExplore>({
    AMCList: [],
    schemeType: []
  });
  const [checkedSchemes, setCheckedSchemes] = useState<SchemeList[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const location = useLocation();

  const routeState = location.state as {
    investorId?: string;
    investorName?: string;
    modeOfHolding?: number | null;
  } | null;

  // New state for fund categories
  const [selectedFundCategories, setSelectedFundCategories] = useState({
    Equity: false,
    Debt: false,
    Liquid: false,
    Hybrid: false,
  });

  const handleChange = (event: any, newValue: any) => {
    setValue(newValue);
    const temp = sub_asset_classes?.filter((ele) => ele.Sub_AssetclassName === newValue);
    setSelectedSubAssetClass(temp ? temp[0] : selectedSubAssetClass);
  };

  const handleModalOpen = () => setOpenModal(true);
  const handleModalClose = () => {
    setOpenModal(false);
    setCheckedSchemes([]);
  };

  const OnSchemeSelection = (schemeId: number) => {
    const selectedScheme = schemeList?.find((val) => val.SchemeID === schemeId);
    if (selectedScheme) {
      setCheckedSchemes(prev => [...prev, selectedScheme]);
      dispatch({ type: SET_SCHEME_FOR_BASKET, value: [...globalVariables, selectedScheme] });
    }
  };

  const OnSchemeDeSelection = (schemeId: number) => {
    setCheckedSchemes(prev => prev.filter((val) => val.SchemeID !== schemeId));
    const updatedGlobal = globalVariables.filter((val: any) => val.SchemeID !== schemeId);
    dispatch({ type: SET_SCHEME_FOR_BASKET, value: updatedGlobal });
  };

  useEffect(() => {
    const uniqueAMCIds = new Set<number>();
    const AmcList: { AMC: string; AMCId: number }[] = [];

    schemeList?.forEach((value: SchemeList) => {
      if (!uniqueAMCIds.has(value.AmcID)) {
        AmcList.push({ AMC: value.AMC, AMCId: value.AmcID });
        uniqueAMCIds.add(value.AmcID);
      }
    });
    setAMCList(AmcList.sort((a: any, b: any) => a.AMC.toLowerCase().localeCompare(b.AMC.toLowerCase())));
  }, [schemeList]);

  const filteredSchemeList = useMemo(() => {
    if (!schemeList) return [];

    const noFilterApplied = filterData.AMCList.length === 0 && filterData.schemeType.length === 0 && debouncedSearch.trim() === '' && !Object.values(selectedFundCategories).some(Boolean);

    let filtered = schemeList;

    // Filter based on selected fund categories if any
    const fundCategoryFilters = Object.entries(selectedFundCategories)
      .filter(([_, isSelected]) => isSelected)
      .map(([category]) => category);

    if (fundCategoryFilters.length > 0) {
      filtered = filtered.filter(scheme => fundCategoryFilters.includes(scheme.AssetClassName));
    }

    if (noFilterApplied) {
      // Initial filter: schemes with AssetClassName 'Equity' AND Dividend options
      filtered = schemeList.filter(scheme =>
        scheme.AssetClassName === 'Equity' &&
        (scheme.Dividendreinvestment === 1 || scheme.DividendPayout === 1)
      );
    } else {
      if (filterData.AMCList.length > 0) {
        filtered = filtered.filter(scheme =>
          filterData.AMCList.some(selectedAMC => selectedAMC.AMCId === scheme.AmcID)
        );
      }
      if (debouncedSearch.trim() !== '') {
        const lowerSearch = debouncedSearch.toLowerCase();
        filtered = filtered.filter(item =>
          Object.values(item).some(value =>
            String(value).toLowerCase().includes(lowerSearch)
          )
        );
      }
      if (filterData.schemeType.length > 0) {
        filtered = filtered.filter(scheme => {
          const isGrowth = filterData.schemeType.includes('Growth');
          const isDiv = filterData.schemeType.includes('Dividend');
          if (isGrowth && isDiv) return true;
          if (isGrowth) return scheme.Growthoption === 1;
          if (isDiv) return scheme.Dividendreinvestment === 1 || scheme.DividendPayout === 1;
          return true;
        });
      }
    }

    return filtered;

  }, [schemeList, filterData, debouncedSearch, selectedFundCategories]);

  const handleFundCategoryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setSelectedFundCategories(prev => ({ ...prev, [name]: checked }));
  };

  const finalModeOfHolding =
    modeOfHolding !== null && modeOfHolding !== undefined
      ? modeOfHolding
      : routeState?.modeOfHolding ?? null;

  return (
    <div style={{ width: "100%" }}>
      <FormControl sx={{ width: 225, mx: 3, mb: 2 }} variant="outlined">
        <OutlinedInput
          value={search}
          onChange={({ target: { value } }) => setSearch(value)}
          sx={{ '& .MuiOutlinedInput-input': { py: 1, fontSize: 13 } }}
          id="search-field"
          placeholder="Search by Scheme Name"
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          }
        />
      </FormControl>

      {/* Fund Categories & Buttons Section */}
      <Box sx={{
        mx: 3,
        mb: 2,
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
      }}>
        <Box>
          <Typography variant="h6">Fund Categories</Typography>
          <FormControlLabel
            control={<Checkbox checked={selectedFundCategories.Equity} onChange={handleFundCategoryChange} name="Equity" />}
            label="Equity"
          />
          <FormControlLabel
            control={<Checkbox checked={selectedFundCategories.Debt} onChange={handleFundCategoryChange} name="Debt" />}
            label="Debt"
          />
          <FormControlLabel
            control={<Checkbox checked={selectedFundCategories.Liquid} onChange={handleFundCategoryChange} name="Liquid" />}
            label="Liquid"
          />
          <FormControlLabel
            control={<Checkbox checked={selectedFundCategories.Hybrid} onChange={handleFundCategoryChange} name="Hybrid" />}
            label="Hybrid"
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Badge badgeContent={globalVariables.length} color="error" overlap="circular"
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <Button
              sx={{
                height: '27px',
                color: 'white',
                fontSize: '13px',
                cursor: 'pointer',
                borderRadius: '5px',
                bgcolor: '#337FC9',
                '&:hover': { backgroundColor: '#337FC9' },
                padding: { xs: '6px 12px', sm: '6px 16px' },
              }}
              onClick={handleModalOpen}>
              {globalVariables.length < 2 ? 'Transaction' : 'Recommend'}
            </Button>
          </Badge>
          <Box>
            <Filters AMCList={AMCList} filterData={filterData} setFilterData={setFilterData} />
          </Box>
        </Box>
      </Box>

      <Box sx={{
        width: '100%',
        '& .MuiTabPanel-root': { py: 2, px: 0 },
        '& .MuiTab-root': {
          color: '#A1A2A2',
          opacity: 0.8,
          fontSize: 17,
          lineHeight: '24px',
          textTransform: 'capitalize',
          px: { xs: 2, md: 3, lg: 5 },
          '&.Mui-selected': {
            color: '#4B81B1',
          },
        },
        '& .MuiTabs-indicator': {
          height: 3,
          background: '#4B81B1',
        },
      }}>
        <Divider />
        <TabContext value={value}>

          <Divider />
          <Box sx={{ px: 3 }}>
            {filteredSchemeList.length > 0 ? (
              filteredSchemeList
                .sort((a, b) => a.SchemeName.toLowerCase().localeCompare(b.SchemeName.toLowerCase()))
                .map((val: SchemeList) => (
                  <React.Fragment key={val.SchemeID}>
                    <SubFundCategoryCard
                      onSchemeSelection={OnSchemeSelection}
                      schemeDetail={val}
                      OnSchemeDeSelection={OnSchemeDeSelection}
                      handleSetViewSchemeDetails={handleSetViewSchemeDetails}
                      allocation={false}
                      selectedSchemes={checkedSchemes}
                      openModal={openModal}
                    />
                    <Divider />
                  </React.Fragment>
                ))
            ) : (
              <Typography variant="h6" sx={{ textAlign: 'center', mt: 2 }}>
                No Scheme Available
              </Typography>
            )}
          </Box>

        </TabContext>
      </Box>

      <Modal
        open={openModal}
        onClose={handleModalClose}
        aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description"
      >
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)', width: 1000,
          bgcolor: 'background.paper', borderRadius: '16px',
          boxShadow: 24, border: 'none', py: 3, height: '500px',
        }}>
          <RecommendedSchemesModal
            handleModalClose={handleModalClose}
            selectedSchemes={checkedSchemes}
            OnSchemeDeSelection={OnSchemeDeSelection}
            investorId={investorId}
            investorName={investorName}
            modeOfHolding={finalModeOfHolding}
            isRecommended={(val: boolean) => { /* Handle recommended state */ }}
          />
        </Box>
      </Modal>
    </div>
  );
}